/**
 * Product Landing Page JavaScript - Dawn Theme Compatible
 * Fixed slider animation and navigation issues
 */

class ProductLandingPage {
  constructor(container) {
    this.container = container;
    this.selectedVariantId = this.container.dataset.selectedVariantId || null;
    
    // Initialize components
    this.initSliders();
    this.initVariantSelection();
    this.initAddToCart();
    this.initStickyBar();
    this.initDeliveryDate();
    this.initFAQAccordion();
    
    // Auto-select first available variant if none selected
    this.autoSelectFirstVariant();
    
    // Bind events
    this.bindEvents();
  }

  initSliders() {
    this.thumbnailSlider = this.container.querySelector('[data-thumbnails-slider]');
    this.thumbnailContainer = this.container.querySelector('[data-thumbnails-container]');
    this.mainSlider = this.container.querySelector('[data-main-slider]');
    this.navPrev = this.container.querySelector('[data-nav-prev]');
    this.navNext = this.container.querySelector('[data-nav-next]');
    
    this.currentSlide = 0;
    this.totalSlides = this.mainSlider?.querySelectorAll('.product-landing__main-item').length || 0;
    
    // Thumbnail slider properties
    this.currentThumbnailIndex = 0;
    this.thumbnailsVisible = 4;
    this.totalThumbnails = this.thumbnailContainer?.querySelectorAll('.product-landing__thumbnail-item').length || 0;
    this.maxThumbnailIndex = Math.max(0, this.totalThumbnails - this.thumbnailsVisible);
    
    // FIX 1: Initialize without animation on page load
    this.initializeSliderWithoutAnimation();
    
    // FIX 2: Add touch/swipe support for mobile
    this.initTouchSupport();
    
    this.updateThumbnailPosition();
  }

  // FIX 1: Initialize slider without showing slide animation on page load
  initializeSliderWithoutAnimation() {
    const mainItems = this.mainSlider?.querySelectorAll('.product-landing__main-item');
    const thumbnails = this.thumbnailContainer?.querySelectorAll('.product-landing__thumbnail-item');
    
    if (mainItems) {
      mainItems.forEach((item, index) => {
        // Remove transition temporarily for initial setup
        item.style.transition = 'none';
        
        if (index === 0) {
          item.classList.add('product-landing__main-item--active');
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
          item.classList.remove('product-landing__main-item--active');
        }
      });
      
      // Re-enable transitions after a brief delay
      setTimeout(() => {
        mainItems.forEach(item => {
          item.style.transition = '';
        });
      }, 100);
    }
    
    // Set first thumbnail as active
    if (thumbnails) {
      thumbnails[0]?.classList.add('product-landing__thumbnail-item--active');
    }
    
    // FIX 3: Update navigation button states
    this.updateNavigationButtons();
  }

  // FIX 2: Add touch/swipe support for mobile
  initTouchSupport() {
    if (!this.mainSlider) return;
    
    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartY = 0;
    let touchEndY = 0;
    let isSwiping = false;
    
    this.mainSlider.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isSwiping = true;
    }, { passive: true });
    
    this.mainSlider.addEventListener('touchmove', (e) => {
      if (!isSwiping) return;
      
      touchEndX = e.touches[0].clientX;
      touchEndY = e.touches[0].clientY;
      
      // Prevent vertical scrolling if horizontal swipe is detected
      const deltaX = Math.abs(touchEndX - touchStartX);
      const deltaY = Math.abs(touchEndY - touchStartY);
      
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }, { passive: false });
    
    this.mainSlider.addEventListener('touchend', (e) => {
      if (!isSwiping) return;
      isSwiping = false;
      
      const deltaX = touchStartX - touchEndX;
      const deltaY = Math.abs(touchStartY - touchEndY);
      const minSwipeDistance = 50;
      
      // Only trigger if horizontal swipe is greater than vertical
      if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          // Swiped left - go to next slide
          this.handleNextSlide();
        } else {
          // Swiped right - go to previous slide  
          this.handlePrevSlide();
        }
      }
    }, { passive: true });
  }

  initVariantSelection() {
    this.colorSwatches = this.container.querySelectorAll('.product-landing__color-swatch');
    this.selectedColorDisplay = this.container.querySelector('[data-selected-color]');
    this.stickyColorDisplay = this.container.querySelector('[data-sticky-color]');
  }

  initAddToCart() {
    this.addToCartBtn = this.container.querySelector('[data-add-to-cart]');
    this.stickyAddToCartBtn = this.container.querySelector('[data-sticky-add-to-cart]');
    this.productForm = this.container.querySelector('product-form form');
    this.cartPrice = this.container.querySelector('[data-cart-price]');
    this.cartCompare = this.container.querySelector('[data-cart-compare]');
    
    // Get the main submit button for sticky button to trigger
    const sectionId = this.container.closest('.shopify-section')?.id.replace('shopify-section-', '');
    this.mainSubmitBtn = this.container.querySelector(`#ProductSubmitButton-${sectionId}`) || 
                       this.container.querySelector('.landing-page-add-to-cart');
  }

  initStickyBar() {
    this.stickyBar = this.container.querySelector('[data-sticky-bar]');
    this.productInfo = this.container.querySelector('.product-landing__info');
    
    // Set up intersection observer for sticky bar
    if (this.stickyBar && this.productInfo) {
      this.observeProductInfo();
    }
  }

  initDeliveryDate() {
    const deliveryDateEl = this.container.querySelector('[data-delivery-date]');
    
    if (deliveryDateEl) {
      const today = new Date();
      today.setDate(today.getDate() + 7);
      
      const day = today.getDate();
      const month = today.toLocaleString('default', { month: 'long' });
      
      const ordinal = this.getOrdinalSuffix(day);
      const formattedDate = `${month} ${day}${ordinal}`;
      
      deliveryDateEl.textContent = formattedDate;
    }
  }

  initFAQAccordion() {
    // Convert all FAQ items from details to custom accordion
    const faqItems = this.container.querySelectorAll('.product-landing__faq-item');
    
    faqItems.forEach((item, index) => {
      this.convertToCustomAccordion(item, index);
    });
  }

  // Auto-select first available variant with native form sync
  autoSelectFirstVariant() {
    // Check if a variant is already selected
    if (this.selectedVariantId) {
      // Still update the native form in case it's out of sync
      this.updateNativeProductForm(this.selectedVariantId);
      return;
    }

    // Find the first available (not disabled) color swatch
    const firstAvailableSwatch = this.container.querySelector('.product-landing__color-swatch:not(.product-landing__color-swatch--sold-out):not([disabled])');
    
    if (firstAvailableSwatch) {
      // Simulate a click on the first available variant
      firstAvailableSwatch.click();
    } else {
      // If no available swatches found, try to get the first swatch regardless
      const firstSwatch = this.container.querySelector('.product-landing__color-swatch');
      if (firstSwatch) {
        // Set the variant ID directly
        this.selectedVariantId = firstSwatch.dataset.variantId;
        
        // CRITICAL: Update the native form with the selected variant
        this.updateNativeProductForm(this.selectedVariantId);
        
        // Add active class
        firstSwatch.classList.add('product-landing__color-swatch--active');

        // Update color displays
        const colorName = firstSwatch.dataset.colorName;
        if (this.selectedColorDisplay) {
          this.selectedColorDisplay.textContent = colorName;
        }
        
        if (this.stickyColorDisplay) {
          this.stickyColorDisplay.textContent = colorName;
        }
      }
    }
  }

  // Update Dawn's native product form with selected variant
  updateNativeProductForm(variantId) {
    // Find the Dawn theme's native product form elements
    const productForm = this.container.querySelector('product-form');
    const variantInput = this.container.querySelector('.product-variant-id');
    const submitButton = this.mainSubmitBtn;
    
    if (variantInput) {
      // Update the hidden variant ID input
      variantInput.value = variantId;
      
      // Remove disabled state if it was disabled
      variantInput.disabled = false;
      
      // Trigger change event to notify the product form
      const changeEvent = new Event('change', { bubbles: true });
      variantInput.dispatchEvent(changeEvent);
    }
    
    // Enable the submit button if it was disabled
    if (submitButton) {
      submitButton.disabled = false;
      
      // Update button text if needed
      const buttonText = submitButton.querySelector('span');
      if (buttonText && (buttonText.textContent.includes('Unavailable') || buttonText.textContent.includes('Sold out'))) {
        buttonText.textContent = 'Add to cart';
      }
    }
    
    // If the product-form web component exists, ensure it's updated
    if (productForm) {
      // Dispatch a custom event that the product form can listen to
      productForm.dispatchEvent(new CustomEvent('variant-changed', {
        detail: { variantId: variantId }
      }));
      
      // Try to call native methods if they exist
      if (typeof productForm.updateOptions === 'function') {
        productForm.updateOptions();
      }
      
      if (typeof productForm.updateMasterButton === 'function') {
        productForm.updateMasterButton();
      }
    }
  }

  convertToCustomAccordion(detailsElement, index) {
    const question = detailsElement.querySelector('.product-landing__faq-question');
    const answer = detailsElement.querySelector('.product-landing__faq-answer');
    
    if (!question || !answer) return;
    
    // Create new accordion structure
    const accordionItem = document.createElement('div');
    accordionItem.className = 'product-landing__accordion-item';
    accordionItem.dataset.index = index;
    
    const accordionHeader = document.createElement('div');
    accordionHeader.className = 'product-landing__accordion-header';
    accordionHeader.innerHTML = question.innerHTML;
    
    const accordionContent = document.createElement('div');
    accordionContent.className = 'product-landing__accordion-content';
    
    const accordionBody = document.createElement('div');
    accordionBody.className = 'product-landing__accordion-body';
    accordionBody.innerHTML = answer.innerHTML;
    
    accordionContent.appendChild(accordionBody);
    accordionItem.appendChild(accordionHeader);
    accordionItem.appendChild(accordionContent);
    
    // Replace the original details element
    detailsElement.parentNode.replaceChild(accordionItem, detailsElement);
    
    // Add click event
    accordionHeader.addEventListener('click', () => {
      this.toggleAccordion(accordionItem, accordionContent);
    });
    
    // Set initial state
    accordionContent.style.height = '0px';
    accordionContent.style.overflow = 'hidden';
  }

  toggleAccordion(accordionItem, content) {
    const isOpen = accordionItem.classList.contains('accordion-open');
    
    if (isOpen) {
      this.closeAccordion(accordionItem, content);
    } else {
      // Close all other accordions (optional)
      this.closeAllAccordions(accordionItem);
      this.openAccordion(accordionItem, content);
    }
  }

  openAccordion(accordionItem, content) {
    const body = content.querySelector('.product-landing__accordion-body');
    
    // Add opening class
    accordionItem.classList.add('accordion-opening');
    accordionItem.classList.remove('accordion-closing');
    
    // Get natural height
    const naturalHeight = body.scrollHeight;
    
    // Set height and trigger animation
    content.style.height = naturalHeight + 'px';
    
    // Update icon state
    this.updateAccordionIcon(accordionItem, true);
    
    // After animation completes
    setTimeout(() => {
      accordionItem.classList.remove('accordion-opening');
      accordionItem.classList.add('accordion-open');
      content.style.height = 'auto'; // Allow dynamic resizing
    }, 350);
  }

  closeAccordion(accordionItem, content) {
    const body = content.querySelector('.product-landing__accordion-body');
    
    // Get current height
    const currentHeight = content.scrollHeight;
    content.style.height = currentHeight + 'px';
    
    // Add closing class
    accordionItem.classList.add('accordion-closing');
    accordionItem.classList.remove('accordion-opening', 'accordion-open');
    
    // Force reflow then animate to 0
    content.offsetHeight;
    content.style.height = '0px';
    
    // Update icon state
    this.updateAccordionIcon(accordionItem, false);
    
    // After animation completes
    setTimeout(() => {
      accordionItem.classList.remove('accordion-closing');
    }, 350);
  }

  closeAllAccordions(currentItem) {
    const allAccordions = this.container.querySelectorAll('.product-landing__accordion-item');
    
    allAccordions.forEach(item => {
      if (item !== currentItem && item.classList.contains('accordion-open')) {
        const content = item.querySelector('.product-landing__accordion-content');
        this.closeAccordion(item, content);
      }
    });
  }

  updateAccordionIcon(accordionItem, isOpen) {
    const icon = accordionItem.querySelector('.product-landing__faq-icon');
    if (!icon) return;
    
    if (isOpen) {
      icon.classList.add('icon-open');
    } else {
      icon.classList.remove('icon-open');
    }
  }

  bindEvents() {
    // Thumbnail slider events
    if (this.thumbnailSlider) {
      this.thumbnailSlider.addEventListener('click', this.handleThumbnailClick.bind(this));
    }

    // Navigation events
    if (this.navPrev) {
      this.navPrev.addEventListener('click', this.handlePrevSlide.bind(this));
    }
    
    if (this.navNext) {
      this.navNext.addEventListener('click', this.handleNextSlide.bind(this));
    }

    // Variant selection events
    this.colorSwatches.forEach(swatch => {
      swatch.addEventListener('click', this.handleVariantChange.bind(this));
    });

    // Add to cart events
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
    }
    
    // Sticky button handler
    if (this.stickyAddToCartBtn) {
      this.stickyAddToCartBtn.addEventListener('click', this.handleStickyAddToCart.bind(this));
    }

    // Keyboard navigation
    document.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
  }

  handleThumbnailClick(event) {
    const thumbnail = event.target.closest('.product-landing__thumbnail-item');
    if (!thumbnail) return;

    const slideIndex = parseInt(thumbnail.dataset.thumbnail);
    this.goToSlide(slideIndex);
  }

  // FIX 3: Updated navigation with boundary checks
  handlePrevSlide() {
    if (this.totalSlides <= 1 || this.currentSlide === 0) return;
    
    this.currentSlide = this.currentSlide - 1;
    this.updateSliderState();
    this.ensureThumbnailVisible(this.currentSlide);
    this.updateNavigationButtons();
  }

  handleNextSlide() {
    if (this.totalSlides <= 1 || this.currentSlide === this.totalSlides - 1) return;
    
    this.currentSlide = this.currentSlide + 1;
    this.updateSliderState();
    this.ensureThumbnailVisible(this.currentSlide);
    this.updateNavigationButtons();
  }

  // FIX 3: Update navigation button states based on current slide
  updateNavigationButtons() {
    if (this.navPrev) {
      if (this.currentSlide === 0 || this.totalSlides <= 1) {
        this.navPrev.disabled = true;
        this.navPrev.style.opacity = '0.5';
        this.navPrev.style.cursor = 'not-allowed';
      } else {
        this.navPrev.disabled = false;
        this.navPrev.style.opacity = '1';
        this.navPrev.style.cursor = 'pointer';
      }
    }
    
    if (this.navNext) {
      if (this.currentSlide === this.totalSlides - 1 || this.totalSlides <= 1) {
        this.navNext.disabled = true;
        this.navNext.style.opacity = '0.5';
        this.navNext.style.cursor = 'not-allowed';
      } else {
        this.navNext.disabled = false;
        this.navNext.style.opacity = '1';
        this.navNext.style.cursor = 'pointer';
      }
    }
  }

  goToSlide(index) {
    if (index >= 0 && index < this.totalSlides && index !== this.currentSlide) {
      this.currentSlide = index;
      this.updateSliderState();
      this.ensureThumbnailVisible(index);
      this.updateNavigationButtons();
    }
  }

  updateSliderState() {
    // Update main slider with consistent positioning
    const mainItems = this.mainSlider?.querySelectorAll('.product-landing__main-item');
    
    mainItems?.forEach((item, index) => {
      // Remove all animation classes first
      item.classList.remove('product-landing__main-item--active', 'product-landing__main-item--slide-left', 'product-landing__main-item--slide-right');
      
      // Reset any inline styles that might interfere
      item.style.display = '';
      
      if (index === this.currentSlide) {
        // Current slide: active and visible
        item.classList.add('product-landing__main-item--active');
        item.style.opacity = '1';
        item.style.transform = 'translateX(0)';
      } else if (index < this.currentSlide) {
        // Previous slides: slide to left and hidden
        item.classList.add('product-landing__main-item--slide-left');
        item.style.opacity = '0';
        item.style.transform = 'translateX(-100%)';
      } else {
        // Next slides: slide to right and hidden
        item.classList.add('product-landing__main-item--slide-right');
        item.style.opacity = '0';
        item.style.transform = 'translateX(100%)';
      }
    });

    // Update thumbnails with active state
    const thumbnails = this.thumbnailContainer?.querySelectorAll('.product-landing__thumbnail-item');
    thumbnails?.forEach((thumbnail, index) => {
      thumbnail.classList.toggle('product-landing__thumbnail-item--active', index === this.currentSlide);
    });
  }

  ensureThumbnailVisible(thumbnailIndex) {
    const visibleStart = this.currentThumbnailIndex;
    const visibleEnd = this.currentThumbnailIndex + this.thumbnailsVisible - 1;
    
    if (thumbnailIndex < visibleStart) {
      // Scroll up to show this thumbnail
      this.currentThumbnailIndex = thumbnailIndex;
      this.updateThumbnailPosition();
    } else if (thumbnailIndex > visibleEnd) {
      // Scroll down to show this thumbnail
      this.currentThumbnailIndex = Math.min(thumbnailIndex - this.thumbnailsVisible + 1, this.maxThumbnailIndex);
      this.updateThumbnailPosition();
    }
  }

  updateThumbnailPosition() {
    if (!this.thumbnailContainer) return;
    
    const thumbnailHeight = 11; // rem
    const gap = 1.5; // rem
    const moveDistance = (thumbnailHeight + gap) * this.currentThumbnailIndex;
    
    this.thumbnailContainer.style.transform = `translateY(-${moveDistance}rem)`;
  }

  // Handle variant change with native form sync
  handleVariantChange(event) {
    const swatch = event.currentTarget;
    if (swatch.disabled) return;

    // Update active state
    this.colorSwatches.forEach(s => s.classList.remove('product-landing__color-swatch--active'));
    swatch.classList.add('product-landing__color-swatch--active');

    // Update selected variant
    this.selectedVariantId = swatch.dataset.variantId;
    const colorName = swatch.dataset.colorName;

    // Update color displays
    if (this.selectedColorDisplay) {
      this.selectedColorDisplay.textContent = colorName;
    }
    
    if (this.stickyColorDisplay) {
      this.stickyColorDisplay.textContent = colorName;
    }

    // CRITICAL FIX: Update the Dawn theme's native product form
    this.updateNativeProductForm(this.selectedVariantId);

    // Update variant image if available
    const variantImage = swatch.dataset.variantImage;
    
    if (variantImage && variantImage !== '' && variantImage !== 'undefined' && !variantImage.includes('null')) {
      this.updateSliderWithVariantImage(variantImage);
    }

    // Update sticky pricing
    this.updateStickyPricing(swatch);

    // Dispatch custom event for other scripts to listen to
    this.container.dispatchEvent(new CustomEvent('variantChanged', {
      detail: { 
        variantId: this.selectedVariantId, 
        colorName,
        variantImage 
      }
    }));
  }

  // Handle sticky add to cart with reliable success detection
  handleStickyAddToCart(event) {
    event.preventDefault();
    
    const stickyBtn = event.currentTarget;
    const mainBtn = this.mainSubmitBtn;
    
    if (!mainBtn) {
      console.error('Main add to cart button not found');
      this.showError('Unable to add to cart. Please try the main button.');
      return;
    }
    
    // Visual feedback on sticky button
    const originalText = stickyBtn.querySelector('.sticky-button-text')?.textContent || stickyBtn.textContent;
    stickyBtn.classList.add('loading');
    stickyBtn.disabled = true;
    
    // Get initial cart count
    const initialCartCount = this.getCartCount();
    
    // Click the main button
    mainBtn.click();
    
    // Simple polling approach to detect success
    let pollAttempts = 0;
    const maxPolls = 8; // 4 seconds total
    
    const pollForSuccess = () => {
      pollAttempts++;
      const currentCartCount = this.getCartCount();
      
      // Check if cart count increased
      if (currentCartCount > initialCartCount) {
        // Success!
        stickyBtn.classList.remove('loading');
        stickyBtn.classList.add('success');
        stickyBtn.disabled = false;
        
        const buttonText = stickyBtn.querySelector('.sticky-button-text');
        if (buttonText) {
          buttonText.textContent = 'Added!';
        } else {
          stickyBtn.textContent = 'Added!';
        }
        
        // Reset after delay
        setTimeout(() => {
          stickyBtn.classList.remove('success');
          if (buttonText) {
            buttonText.textContent = originalText;
          } else {
            stickyBtn.textContent = originalText;
          }
        }, 1500);
        
        return;
      }
      
      // Continue polling if not maxed out
      if (pollAttempts < maxPolls) {
        setTimeout(pollForSuccess, 500);
      } else {
        // Assume success after timeout (since main button worked)
        // Don't show error, just reset button
        stickyBtn.classList.remove('loading');
        stickyBtn.disabled = false;
        stickyBtn.classList.add('success');
        
        const buttonText = stickyBtn.querySelector('.sticky-button-text');
        if (buttonText) {
          buttonText.textContent = 'Added!';
        } else {
          stickyBtn.textContent = 'Added!';
        }
        
        // Reset after delay
        setTimeout(() => {
          stickyBtn.classList.remove('success');
          if (buttonText) {
            buttonText.textContent = originalText;
          } else {
            stickyBtn.textContent = originalText;
          }
        }, 1500);
      }
    };
    
    // Start polling after a brief delay
    setTimeout(pollForSuccess, 500);
  }

  // Helper method to get current cart count
  getCartCount() {
    // Try multiple selectors for cart count
    const selectors = [
      '.cart-count-bubble',
      '[data-cart-count]',
      '.cart-count',
      '#cart-icon-bubble [data-cart-count]',
      '.header__icon--cart .cart-count-bubble'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        const count = element.textContent?.trim() || 
                     element.getAttribute('data-cart-count') || 
                     element.getAttribute('aria-label')?.match(/\d+/)?.[0] || '0';
        const parsedCount = parseInt(count) || 0;
        if (parsedCount >= 0) return parsedCount;
      }
    }
    
    // Fallback: try to get count from cart drawer if visible
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.hasAttribute('open')) {
      const items = cartDrawer.querySelectorAll('.cart-item, [data-cart-item]');
      return items.length;
    }
    
    return 0;
  }

  // Update sticky pricing display
  updateStickyPricing(swatch) {
    // Get variant pricing from data attributes if available
    const variantPrice = swatch.dataset.variantPrice;
    const variantComparePrice = swatch.dataset.variantComparePrice;
    
    if (variantPrice) {
      const stickyCurrentPrice = this.container.querySelector('[data-sticky-current-price]');
      const stickyComparePrice = this.container.querySelector('[data-sticky-compare-price]');
      
      if (stickyCurrentPrice) {
        stickyCurrentPrice.textContent = variantPrice;
      }
      
      if (stickyComparePrice) {
        if (variantComparePrice && variantComparePrice !== variantPrice) {
          stickyComparePrice.textContent = variantComparePrice;
          stickyComparePrice.style.display = 'inline';
        } else {
          stickyComparePrice.style.display = 'none';
        }
      }
    }
  }

  updateSliderWithVariantImage(imageUrl) {
    // Fix protocol-less URLs
    let fixedImageUrl = imageUrl;
    if (imageUrl.startsWith('//')) {
      fixedImageUrl = 'https:' + imageUrl;
    }
    
    // Update first main image
    const firstMainImg = this.mainSlider?.querySelector('.product-landing__main-item--first .product-landing__main-img');
    
    if (firstMainImg && fixedImageUrl) {
      // Store original src for potential restoration
      if (!firstMainImg.dataset.originalSrc) {
        firstMainImg.dataset.originalSrc = firstMainImg.src;
      }
      if (!firstMainImg.dataset.originalSrcset) {
        firstMainImg.dataset.originalSrcset = firstMainImg.srcset;
      }
      
      // Update both src and srcset
      firstMainImg.src = fixedImageUrl;
      
      // Create new srcset with different sizes
      const baseUrl = fixedImageUrl.split('?')[0];
      const timestamp = fixedImageUrl.includes('?v=') ? '?v=' + fixedImageUrl.split('?v=')[1] : '';
      
      const newSrcset = `${baseUrl}${timestamp}&width=352 352w, ${baseUrl}${timestamp}&width=800 800w`;
      firstMainImg.srcset = newSrcset;
    }

    // Update first thumbnail image
    const firstThumbnailImg = this.thumbnailContainer?.querySelector('.product-landing__thumbnail-item:first-child .product-landing__thumbnail-img');
    
    if (firstThumbnailImg && fixedImageUrl) {
      // Store original src for potential restoration
      if (!firstThumbnailImg.dataset.originalSrc) {
        firstThumbnailImg.dataset.originalSrc = firstThumbnailImg.src;
      }
      if (!firstThumbnailImg.dataset.originalSrcset) {
        firstThumbnailImg.dataset.originalSrcset = firstThumbnailImg.srcset;
      }
      
      // Create thumbnail URL properly
      let thumbnailUrl;
      if (fixedImageUrl.includes('?')) {
        if (fixedImageUrl.includes('width=')) {
          thumbnailUrl = fixedImageUrl.replace(/width=\d+/, 'width=150');
        } else {
          thumbnailUrl = fixedImageUrl + '&width=150';
        }
      } else {
        thumbnailUrl = fixedImageUrl + '?width=150';
      }
      
      // Update both src and srcset for thumbnail
      firstThumbnailImg.src = thumbnailUrl;
      firstThumbnailImg.srcset = `${thumbnailUrl} 150w`;
    }

    // Reset to first slide to show the updated variant image
    this.goToSlide(0);

    // Add a subtle animation to indicate the change
    this.animateImageChange(firstMainImg);
  }

  animateImageChange(imgElement) {
    if (!imgElement) return;
    
    imgElement.style.opacity = '0.7';
    imgElement.style.transition = 'opacity 0.3s ease';
    
    setTimeout(() => {
      imgElement.style.opacity = '1';
      setTimeout(() => {
        imgElement.style.transition = '';
      }, 300);
    }, 50);
  }

  async handleAddToCart(event) {
    const button = event.currentTarget;
    const originalText = button.textContent;
    
    // Better error handling with auto-selection fallback
    if (!this.selectedVariantId) {
      // Try to auto-select first variant as fallback
      this.autoSelectFirstVariant();
      
      // Check again after auto-selection
      if (!this.selectedVariantId) {
        this.showError('Please select a variant');
        return;
      }
    }

    // Update button state
    button.disabled = true;
    button.textContent = 'Adding...';
    button.classList.add('loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: this.selectedVariantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Show success immediately
      button.textContent = 'Added!';
      button.classList.remove('loading');
      button.classList.add('success');
      
      // Open and update cart drawer properly
      await this.openCartDrawer();
      
      // Dispatch success event
      this.container.dispatchEvent(new CustomEvent('productAddedToCart', {
        detail: { variantId: this.selectedVariantId, quantity: 1 }
      }));

      // Reset button after showing success
      setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
        button.classList.remove('success');
      }, 1500);

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError('Failed to add product to cart. Please try again.');
      
      // Reset button state on error
      button.disabled = false;
      button.textContent = originalText;
      button.classList.remove('loading');
    }
  }

  async openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    try {
      // First, let's get the current cart data to ensure we have the latest state
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // Check if cart actually has items
      if (cartData.items.length === 0) {
        console.log('Cart is empty, something went wrong');
        return;
      }

      // Open the drawer first for immediate feedback
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('animate', 'active');
      
      // Now fetch the updated cart drawer HTML with proper sections
      const sectionsResponse = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      const sectionsData = await sectionsResponse.json();
      
      if (sectionsData['cart-drawer']) {
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(sectionsData['cart-drawer'], 'text/html');
        
        // Find the new cart drawer content
        const newCartDrawer = newDoc.querySelector('cart-drawer');
        const newDrawerInner = newDoc.querySelector('#CartDrawer .drawer__inner');
        
        if (newCartDrawer && newDrawerInner) {
          // Update the inner content only
          const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');
          if (currentDrawerInner) {
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
          } else {
            // If drawer inner doesn't exist, create it
            cartDrawer.innerHTML = newCartDrawer.innerHTML;
          }
          
          // Re-bind necessary event listeners
          this.rebindCartDrawerEvents(cartDrawer);
        }
      }
      
      // Update cart count in header
      if (sectionsData['cart-icon-bubble']) {
        const cartIconSection = document.getElementById('cart-icon-bubble');
        if (cartIconSection) {
          const parser = new DOMParser();
          const newDoc = parser.parseFromString(sectionsData['cart-icon-bubble'], 'text/html');
          const newCartIcon = newDoc.querySelector('.shopify-section');
          if (newCartIcon) {
            cartIconSection.innerHTML = newCartIcon.innerHTML;
          }
        }
      }
      
      // Ensure drawer stays open
      setTimeout(() => {
        cartDrawer.setAttribute('open', '');
        if (!cartDrawer.classList.contains('active')) {
          cartDrawer.classList.add('animate', 'active');
        }
      }, 50);
      
    } catch (error) {
      console.error('Failed to update cart drawer:', error);
      // Fallback: still open the drawer
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('animate', 'active');
    }
  }

  rebindCartDrawerEvents(cartDrawer) {
    // Re-bind close button
    const closeButton = cartDrawer.querySelector('.drawer__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        cartDrawer.removeAttribute('open');
        cartDrawer.classList.remove('animate', 'active');
      });
    }
    
    // Re-bind overlay click
    const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        cartDrawer.removeAttribute('open');
        cartDrawer.classList.remove('animate', 'active');
      });
    }
    
    // Re-bind escape key
    const handleEscape = (evt) => {
      if (evt.code === 'Escape' && cartDrawer.hasAttribute('open')) {
        cartDrawer.removeAttribute('open');
        cartDrawer.classList.remove('animate', 'active');
      }
    };
    
    document.removeEventListener('keyup', handleEscape);
    document.addEventListener('keyup', handleEscape);
  }

  observeProductInfo() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.stickyBar?.classList.remove('product-landing__sticky-bar--visible');
        } else {
          this.stickyBar?.classList.add('product-landing__sticky-bar--visible');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    });

    if (this.productInfo) {
      observer.observe(this.productInfo);
    }
  }

  handleKeyboardNavigation(event) {
    if (!this.container.contains(document.activeElement)) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.handlePrevSlide();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.handleNextSlide();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.handlePrevSlide();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.handleNextSlide();
        break;
      case 'Home':
        event.preventDefault();
        this.goToSlide(0);
        break;
      case 'End':
        event.preventDefault();
        this.goToSlide(this.totalSlides - 1);
        break;
    }
  }

  showError(message) {
    let errorEl = this.container.querySelector('.product-landing__error');
    
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'product-landing__error';
      errorEl.style.cssText = `
        position: fixed;
        top: 2rem;
        right: 2rem;
        background: rgb(var(--color-error, 220, 38, 38));
        color: white;
        padding: 1rem 1.5rem;
        border-radius: var(--border-radius, 0.5rem);
        z-index: 1000;
        font-size: 1.4rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      `;
      document.body.appendChild(errorEl);
    }

    errorEl.textContent = message;
    errorEl.style.display = 'block';

    setTimeout(() => {
      if (errorEl && errorEl.parentNode) {
        errorEl.parentNode.removeChild(errorEl);
      }
    }, 4000);
  }

  getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  updateVariant(variantId) {
    this.selectedVariantId = variantId;
    
    const targetSwatch = this.container.querySelector(`[data-variant-id="${variantId}"]`);
    if (targetSwatch) {
      targetSwatch.click();
    }
  }

  updatePrice(price, comparePrice = null) {
    if (this.cartPrice) {
      this.cartPrice.textContent = price;
    }
    
    if (this.cartCompare && comparePrice) {
      this.cartCompare.textContent = comparePrice;
      this.cartCompare.style.display = 'inline';
    } else if (this.cartCompare) {
      this.cartCompare.style.display = 'none';
    }
  }

  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    const errorEl = document.querySelector('.product-landing__error');
    if (errorEl && errorEl.parentNode) {
      errorEl.parentNode.removeChild(errorEl);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const productLandingContainers = document.querySelectorAll('.product-landing');
  
  productLandingContainers.forEach(container => {
    new ProductLandingPage(container);
  });
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductLandingPage;
}